import { z } from "zod";

export const deploymentTierNames = ["preview", "beta", "production"] as const;
export type DeploymentTierName = (typeof deploymentTierNames)[number];

export type Environment =
  | { kind: "local"; stack?: LocalStack }
  | { kind: "beta" }
  | { kind: "production" }
  | { kind: "preview"; pr: number };

const PullRequestNumber = z.number().int().positive();

const hostname = z
  .string()
  .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/, "hostname");

const cloudflareName = z.string().regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, "cloudflare name");

const CloudEnvironment = z.object({
  accountId: z.string().regex(/^[0-9a-f]{32}$/, "cloudflare account id"),
  zoneId: z.string().regex(/^[0-9a-f]{32}$/, "cloudflare zone id"),
  zone: hostname,
  host: hostname,
  planetscaleOrganization: z.string().min(1),
  gamesBucket: cloudflareName,
  agentRunnerWorker: cloudflareName,
  gameServerWorker: cloudflareName,
  presetServerWorker: cloudflareName,
  assetEditorWorker: cloudflareName,
  apiWorker: cloudflareName,
  webWorker: cloudflareName,
  telemetryTailWorker: cloudflareName,
  tilesWorker: cloudflareName,
  apiRateLimitNamespaceBase: z.number().int().positive(),
  telemetryRateLimitNamespace: z.number().int().positive(),
  userContentZone: hostname,
  userContentZoneId: z.string().regex(/^[0-9a-f]{32}$/, "cloudflare zone id"),
  mediaUploadsBucket: cloudflareName,
  privateMediaBucket: cloudflareName,
  mediaBucket: cloudflareName,
  googleCloudProject: z.string().regex(/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/, "google cloud project id"),
  googleCloudProjectNumber: z.string().regex(/^\d+$/, "google cloud project number"),
  googleCloudRegion: z.string().regex(/^[a-z]+-[a-z]+\d$/, "google cloud region"),
});
type CloudEnvironment = z.infer<typeof CloudEnvironment>;

/** The Cloud Run service that runs each durable environment's telemetry collector. */
export const telemetryCollectorService = "otel-collector";

/** Cloud Run's deterministic service URL, known before the service exists. */
function telemetryCollectorOrigin(environment: CloudEnvironment): string {
  return `https://${telemetryCollectorService}-${environment.googleCloudProjectNumber}.${environment.googleCloudRegion}.run.app`;
}

/** The iOS app every environment's builds ship as, which Apple's signed data names it by. */
export const iosBundleId = "co.pax.mobile";

/** The Apple Developer team that owns the app, its Services IDs, and its keys. */
export const appleTeamId = "52NSF267X2";

/** The ClickHouse Cloud organization holding beta's and production's services. */
export const clickhouseOrganizationId = "3bb6e015-77e7-47d3-8d9f-722ca7ea4833";

/** The Slack channels uptime and free credit budget alerts post to. */
export const slackAlertsChannels = { beta: "#pax-alerts-beta", production: "#pax-alerts" } as const;

/** Where a sign-in link opens, and where a service account asks for one, among the API's auth routes. */
export const signInLinkPath = "/api/auth/sign-in-link";

/** How long a sign-in link works. */
export const SIGN_IN_LINK_LIFETIME_MS = 30 * 1000;

/** The verification identifier a sign-in link's token is kept under: its SHA-256, never the token. */
export async function signInLinkIdentifier(token: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(token));
  const hex = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0"));
  return `sign-in-link:${hex.join("")}`;
}

/** Who a sign-in grant signs in, until when, and the nonce that makes it work once. */
export interface SignInGrant {
  email: string;
  expiresAt: number;
  nonce: string;
}

const SIGN_IN_GRANT_PURPOSE = "pax sign-in grant\n";

/**
 * A sign-in link token that names its user and is signed with the environment's
 * `BETTER_AUTH_SECRET`, so whoever holds that secret can write one without the database. Only local
 * development and previews accept one.
 */
export async function createSignInGrant(
  secret: string,
  email: string,
  now = Date.now(),
): Promise<string> {
  const grant: SignInGrant = {
    email: email.trim().toLowerCase(),
    expiresAt: now + SIGN_IN_LINK_LIFETIME_MS,
    nonce: base64Url(crypto.getRandomValues(new Uint8Array(16))),
  };
  const payload = base64Url(new TextEncoder().encode(JSON.stringify(grant)));
  return `${payload}.${await signGrantPayload(secret, payload)}`;
}

/** The grant a token carries, or null when it isn't a grant, its signature is wrong, or it expired. */
export async function readSignInGrant(secret: string, token: string): Promise<SignInGrant | null> {
  const [payload, signature, extra] = token.split(".");
  if (!payload || !signature || extra !== undefined) return null;
  try {
    const valid = await crypto.subtle.verify(
      "HMAC",
      await grantKey(secret, "verify"),
      fromBase64Url(signature),
      grantMessage(payload),
    );
    if (!valid) return null;
    const grant = JSON.parse(new TextDecoder().decode(fromBase64Url(payload))) as SignInGrant;
    return grant.expiresAt > Date.now() ? grant : null;
  } catch {
    return null;
  }
}

async function signGrantPayload(secret: string, payload: string): Promise<string> {
  const signature = await crypto.subtle.sign(
    "HMAC",
    await grantKey(secret, "sign"),
    grantMessage(payload),
  );
  return base64Url(new Uint8Array(signature));
}

function grantKey(secret: string, usage: "sign" | "verify") {
  return crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    [usage],
  );
}

function grantMessage(payload: string): Uint8Array<ArrayBuffer> {
  return new Uint8Array(new TextEncoder().encode(`${SIGN_IN_GRANT_PURPOSE}${payload}`));
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}

function fromBase64Url(text: string): Uint8Array<ArrayBuffer> {
  const binary = atob(text.replaceAll("-", "+").replaceAll("_", "/"));
  return Uint8Array.from(binary, character => character.charCodeAt(0));
}

/**
 * One copy of the local stack on this laptop. The default stack has no name and serves
 * `pax.localhost`; a named stack serves `<name>.pax.localhost` beside it.
 */
export interface LocalStack {
  name?: string;
  /** Spaces a running named stack's ports apart from the others'. The default stack's is 0. */
  slot?: number;
}

export const defaultLocalStack: LocalStack = {};

/** Named stacks take slots 1 to 9, which keeps every app port between 4100 and 4908. */
export const namedLocalStackSlots = 9;

// Each would collide with a host the default stack already serves, or name the default stack.
const reservedLocalStackNames = [
  "default",
  "agent-runner",
  "api",
  "asset-editor",
  "db",
  "docs",
  "game-server",
  "preset-server",
  "tiles",
];

const LocalStackName = z
  .string()
  .regex(
    /^[a-z]([a-z0-9-]{0,10}[a-z0-9])?$/,
    "stack name: 1-12 lowercase letters, digits, or hyphens, starting with a letter",
  )
  .refine(name => !reservedLocalStackNames.includes(name), "that stack name is a local host");

export function parseLocalStackName(value: string): string {
  return LocalStackName.parse(value);
}

/** The stack a process belongs to, from the variables `bun run dev` gives every process it starts. */
export function localStackFromEnv(env: Record<string, string | undefined>): LocalStack {
  const name = env.PAX_STACK?.trim();
  if (!name) return defaultLocalStack;
  if (!env.PAX_STACK_SLOT) return { name: parseLocalStackName(name) };
  const slot = Number(env.PAX_STACK_SLOT);
  if (!Number.isInteger(slot) || slot < 1 || slot > namedLocalStackSlots) {
    throw new Error(`PAX_STACK_SLOT must be a whole number from 1 to ${namedLocalStackSlots}`);
  }
  return { name: parseLocalStackName(name), slot };
}

/** The variables `localStackFromEnv` reads back. */
export function localStackEnv(stack: LocalStack): Record<string, string> {
  if (!stack.name) return {};
  return {
    PAX_STACK: stack.name,
    ...(stack.slot === undefined ? {} : { PAX_STACK_SLOT: String(stack.slot) }),
  };
}

/** A stack's slot, which only a running named stack has. */
function localStackSlot(stack: LocalStack): number {
  if (!stack.name) return 0;
  if (stack.slot === undefined) {
    throw new Error(`the ${stack.name} stack is not running; start it with \`bun run dev\``);
  }
  return stack.slot;
}

/** The domain a stack's hosts sit under: `pax.localhost`, or `<name>.pax.localhost`. */
export function localZone(stack: LocalStack): string {
  return stack.name ? `${stack.name}.pax.localhost` : "pax.localhost";
}

/** The Docker services every stack shares. */
export const localServicePorts = {
  postgres: 5432,
  studio: 4983,
  collectorOtlpGrpc: 4317,
  collectorOtlp: 4318,
  clickhouse: 8123,
  hyperdx: 8080,
} as const;

/** Where a stack's apps listen: 4000 to 4009 for the default stack, 100 higher per slot. */
export function localPorts(stack: LocalStack) {
  const offset = localStackSlot(stack) * 100;
  return {
    web: 4000 + offset,
    api: 4001 + offset,
    gameServer: 4002 + offset,
    agentRunner: 4003 + offset,
    docs: 4004 + offset,
    telemetryTail: 4005 + offset,
    tiles: 4006 + offset,
    presetServer: 4007 + offset,
    assetEditor: 4008 + offset,
    devtoolsEventBus: 4009 + offset,
  };
}

/** Where a stack's Workers take debugger connections: 9231 to 9238 for the default stack, 100 higher per slot. */
export function localDebuggerPorts(stack: LocalStack) {
  const offset = localStackSlot(stack) * 100;
  return {
    gameServer: 9231 + offset,
    api: 9232 + offset,
    web: 9233 + offset,
    agentRunner: 9234 + offset,
    telemetryTail: 9235 + offset,
    tiles: 9236 + offset,
    presetServer: 9237 + offset,
    assetEditor: 9238 + offset,
  };
}

/** A Worker's name in a stack. Named stacks lead with their name, so no stack's starts another's. */
export function localWorkerName(stack: LocalStack, app: string): string {
  return stack.name ? `pax-${stack.name}-${app}` : `pax-${app}`;
}

/** A stack's Postgres database and its ClickHouse `pax` database: `pax`, or `pax_<name>`. */
export function localDatabaseName(stack: LocalStack): string {
  return stack.name ? `pax_${stack.name.replaceAll("-", "_")}` : "pax";
}

export function localDatabaseUrl(stack: LocalStack): string {
  return `postgres://pax:pax@localhost:${localServicePorts.postgres}/${localDatabaseName(stack)}`;
}

export const hyperdriveLocalId = "local";

/** The Docker ClickHouse's login, which `compose.yaml` creates. Every stack shares it. */
export const localClickHouse = {
  url: `http://127.0.0.1:${localServicePorts.clickhouse}`,
  user: "pax",
  password: "pax",
} as const;

export const localToolGroups = ["Sites", "Data", "Workers"] as const;
export type LocalToolGroup = (typeof localToolGroups)[number];

export type LocalTool = {
  group: LocalToolGroup;
  label: string;
  href: string;
  description: string;
};

/**
 * Everything a stack serves for looking around, listed by both sites' Local menus.
 * Local Explorer URLs use 127.0.0.1 because Wrangler rejects its management routes on other hosts.
 */
export function localTools(stack: LocalStack): LocalTool[] {
  const resolved = resolveEnvironment({ kind: "local", stack });
  const ports = localPorts(stack);
  const explorer = (port: number) => `http://127.0.0.1:${port}/cdn-cgi/local/explorer`;
  return [
    { group: "Sites", label: "Web", href: `https://${resolved.host}`, description: "The app." },
    {
      group: "Sites",
      label: "Docs",
      href: `https://docs.${resolved.zone}`,
      description: "These docs, with live reload.",
    },
    {
      group: "Data",
      label: "Drizzle Studio",
      href: "https://db.pax.localhost",
      description: "Browse and edit local Postgres.",
    },
    {
      group: "Data",
      label: "ClickHouse",
      href: `http://127.0.0.1:${localServicePorts.clickhouse}/play?user=pax&password=pax&database=${resolved.clickhouseDatabase}`,
      description: "Run SQL against the telemetry and pax databases.",
    },
    {
      group: "Data",
      label: "HyperDX",
      href: `http://localhost:${localServicePorts.hyperdx}`,
      description: "Search traces, logs, and sessions.",
    },
    {
      group: "Workers",
      label: "API",
      href: explorer(ports.api),
      description: "Workflow runs, R2 objects, and traces.",
    },
    {
      group: "Workers",
      label: "Game server",
      href: explorer(ports.gameServer),
      description: "Game Durable Objects, R2 objects, and traces.",
    },
    {
      group: "Workers",
      label: "Preset server",
      href: explorer(ports.presetServer),
      description: "Draft Durable Objects and traces.",
    },
    {
      group: "Workers",
      label: "Asset editor",
      href: explorer(ports.assetEditor),
      description: "Basemap Durable Objects and traces.",
    },
    {
      group: "Workers",
      label: "Agent runner",
      href: explorer(ports.agentRunner),
      description: "Runner Durable Objects and traces.",
    },
  ];
}

/** OAuth client ids are public: browsers and the app send them with every sign-in. */
const signInClients = {
  beta: {
    googleWebClientId: "562847739374-667pfa7hd9c3ppgokosps1gupidqu0gr.apps.googleusercontent.com",
    googleIosClientId: "562847739374-51rop3vmqjc8g1kp7m2u7ktrpuf9hof5.apps.googleusercontent.com",
    appleServicesId: "co.pax.web.beta",
  },
  production: {
    googleWebClientId: "483475571953-avren86cpjl63pimf751lag0lfsi0or4.apps.googleusercontent.com",
    googleIosClientId: "483475571953-kq32gn1g2ke7cj1mi78pjpeppput0v20.apps.googleusercontent.com",
    appleServicesId: "co.pax.web",
  },
} as const;

/** Castle's publishable keys and reCAPTCHA's web keys, which browsers and the app send openly. */
const riskClients = {
  // Castle's sandbox, and the reCAPTCHA web key the pax/beta stack made, which allows local hosts.
  beta: {
    castlePublishableKey: "pk_GE6cmQzsv9h7md7w7bgUzQWf4eQL96r7",
    recaptchaProjectId: "pax-co-beta",
    recaptchaWebSiteKey: "6LeCzAMuAAAAAOF5U84yZIdUloOsG5JB7iKR7xvQ",
  },
  production: {
    castlePublishableKey: "pk_Q2jKMWVHscTKsMw3W48EFcUmKdvnj7Us",
    recaptchaProjectId: "pax-co-production",
    recaptchaWebSiteKey: "6Lfr9gMuAAAAAPX8vyQxLQKAD3PqM8u2gf3phujZ",
  },
} as const;

/** The keys the API's risk checks run with. Local and preview share beta's. */
export function riskClientSettings(environment: Environment) {
  return riskClients[environment.kind === "production" ? "production" : "beta"];
}

/** Model Studio workspaces, one per region. Their ids name the endpoints; the keys are secret. */
const alibabaWorkspaces = {
  beta: {
    beijing: "ws-fi8co8d5k4uu1bez",
    singapore: "ws-em58r8117sc6jvor",
    virginia: "ws-rgq191bv5j70iqy6",
  },
  production: {
    beijing: "ws-2y9s3uwfa44pr7cl",
    singapore: "ws-ddbrw5nzlhhweg0k",
    virginia: "ws-tohjvzu9nmx71xwe",
  },
} as const;

/** The Model Studio workspaces an environment calls. Local and preview share beta's. */
export function alibabaWorkspaceIds(environment: Environment) {
  return alibabaWorkspaces[environment.kind === "production" ? "production" : "beta"];
}

/**
 * Sign-in behavior that differs by environment. The API receives it as Worker vars, and the iOS
 * app builds its Google sign-in from the same client ids.
 */
export function authSettings(environment: Environment) {
  const beta = resolveEnvironment({ kind: "beta" });
  const local = resolveEnvironment({ kind: "local" });
  const signsInThroughBeta = environment.kind !== "production";
  return {
    ...signInClients[signsInThroughBeta ? "beta" : "production"],
    // Local and preview sign-ins round-trip through beta's registered OAuth callback.
    oauthProxyOrigin: signsInThroughBeta ? `https://${beta.host}` : undefined,
    // Origins beyond PUBLIC_ORIGIN that may start a sign-in or receive its result.
    trustedOrigins: [
      ...(signsInThroughBeta
        ? [`https://${local.host}`, `https://*.${local.zone}`, `https://pr-*.${beta.zone}`]
        : []),
      ...(environment.kind === "local" ? ["exp://", "exp://**"] : []),
    ],
  };
}

const cloudEnvironments: Record<"beta" | "production", CloudEnvironment> = {
  beta: CloudEnvironment.parse({
    accountId: "68ad35c027c699f794ad47b5fa64e7a5",
    zoneId: "86473611e752ec897e22719d9d1bebc0",
    zone: "pax-beta.co",
    host: "pax-beta.co",
    planetscaleOrganization: "team-paxhistoria",
    gamesBucket: "pax-games",
    agentRunnerWorker: "pax-agent-runner",
    gameServerWorker: "pax-game-server",
    presetServerWorker: "pax-preset-server",
    assetEditorWorker: "pax-asset-editor",
    apiWorker: "pax-api",
    webWorker: "pax-web",
    telemetryTailWorker: "pax-telemetry-tail",
    tilesWorker: "pax-tiles",
    apiRateLimitNamespaceBase: 12_000,
    telemetryRateLimitNamespace: 1102,
    userContentZone: "paxusercontent-beta.co",
    userContentZoneId: "2e9d42b4deb5bac3a811e7e638b0a0a4",
    mediaUploadsBucket: "pax-uploads",
    privateMediaBucket: "pax-private-media",
    mediaBucket: "pax-media",
    googleCloudProject: "pax-co-beta",
    googleCloudProjectNumber: "562847739374",
    googleCloudRegion: "us-central1",
  }),
  production: CloudEnvironment.parse({
    accountId: "46306aebf497cbbd45758901e89feba2",
    zoneId: "aee99d1816e1a948fe1e03c6fa5679c3",
    zone: "pax.co",
    host: "pax.co",
    planetscaleOrganization: "team-paxhistoria",
    gamesBucket: "pax-games",
    agentRunnerWorker: "pax-agent-runner",
    gameServerWorker: "pax-game-server",
    presetServerWorker: "pax-preset-server",
    assetEditorWorker: "pax-asset-editor",
    apiWorker: "pax-api",
    webWorker: "pax-web",
    telemetryTailWorker: "pax-telemetry-tail",
    tilesWorker: "pax-tiles",
    apiRateLimitNamespaceBase: 11_000,
    telemetryRateLimitNamespace: 1101,
    userContentZone: "paxusercontent.co",
    userContentZoneId: "b4cc01a9d7f75171c4e268d30fe0b31a",
    mediaUploadsBucket: "pax-uploads",
    privateMediaBucket: "pax-private-media",
    mediaBucket: "pax-media",
    googleCloudProject: "pax-co-production",
    googleCloudProjectNumber: "483475571953",
    googleCloudRegion: "us-central1",
  }),
};

export const previewSubstrate = {
  accountId: cloudEnvironments.beta.accountId,
  zoneId: cloudEnvironments.beta.zoneId,
  zone: cloudEnvironments.beta.zone,
  planetscaleOrganization: cloudEnvironments.beta.planetscaleOrganization,
  databaseName: "pax-beta",
  otlpEndpoint: telemetryCollectorOrigin(cloudEnvironments.beta),
  userContentZoneId: cloudEnvironments.beta.userContentZoneId,
  mediaUploadsBucket: "pax-preview-uploads",
  privateMediaBucket: "pax-preview-private-media",
  mediaBucket: "pax-preview-media",
  mediaHost: `preview.${cloudEnvironments.beta.userContentZone}`,
  googleCloudProject: cloudEnvironments.beta.googleCloudProject,
  googleCloudRegion: cloudEnvironments.beta.googleCloudRegion,
  stripeDefaultPortalConfigurationId: "bpc_1ULv4tE9H7iKz2OTYXYHVCUf",
} as const;

/** The bucket local stacks and previews share for seed files' processed bytes; it never expires. */
export const PROCESSED_BUCKET = "pax-dev-processed";

/** The ops bucket official worlds are baked in: mirrored sources, the drawing cache, and each bake's runs. */
export const WORKBENCH_BUCKET = "pax-ops-workbench";

/** Local development stores media in the beta account; each developer writes under their own prefix. */
export const developmentMediaStorage = {
  accountId: cloudEnvironments.beta.accountId,
  userContentZoneId: cloudEnvironments.beta.userContentZoneId,
  mediaUploadsBucket: "pax-dev-uploads",
  privateMediaBucket: "pax-dev-private-media",
  mediaBucket: "pax-dev-media",
  mediaHost: `dev.${cloudEnvironments.beta.userContentZone}`,
} as const;

// Rate limit namespaces are account-wide; previews share the beta account. Each API namespace
// base leaves room for ten bindings.
const previewApiRateLimitNamespaceBase = 200_000;
const previewTelemetryRateLimitNamespaceBase = 100_000;

/**
 * The API's rate limit bindings, each a fixed budget per minute. A procedure's `cost` picks the
 * binding. Signed-in callers are counted per user; signed-out reads and searches per address,
 * loose enough for many phones behind one carrier address. The session budget guards the sign-in
 * check and anonymous sign-in, which each cost a Castle call, per address.
 */
export const apiRateLimitBindings = {
  API_READ_RATE_LIMIT: { namespaceOffset: 0, limit: 300 },
  API_SEARCH_RATE_LIMIT: { namespaceOffset: 1, limit: 60 },
  API_WRITE_RATE_LIMIT: { namespaceOffset: 2, limit: 60 },
  API_AI_RATE_LIMIT: { namespaceOffset: 3, limit: 60 },
  API_SESSION_RATE_LIMIT: { namespaceOffset: 4, limit: 60 },
} as const;

export interface ResolvedEnvironment {
  name: string;
  accountId?: string;
  zoneId?: string;
  zone: string;
  host: string;
  planetscaleOrganization?: string;
  apiHost: string;
  agentRunnerHost: string;
  gameServerHost: string;
  presetServerHost: string;
  assetEditorHost: string;
  apiOrigin: string;
  agentRunnerOrigin: string;
  gameServerOrigin: string;
  presetServerOrigin: string;
  assetEditorOrigin: string;
  agentRunnerWorker: string;
  gameServerWorker: string;
  presetServerWorker: string;
  assetEditorWorker: string;
  apiWorker: string;
  webWorker: string;
  telemetryTailWorker: string;
  tilesWorker: string;
  /** The tile server's host, which serves published basemaps' tiles at `/{slug}/{z}/{x}/{y}`. */
  tilesHost: string;
  tilesOrigin: string;
  /** The telemetry collector that the telemetry tail and the API send OTLP to; previews use beta's. */
  otlpEndpoint: string;
  gamesBucket: string;
  dbBranch: string;
  databaseName: string;
  /** The ClickHouse database holding the tables Pax migrates itself; each preview gets its own. */
  clickhouseDatabase: string;
  /** The first of the API's rate limit namespaces; each binding adds its own offset. */
  apiRateLimitNamespaceBase: number;
  telemetryRateLimitNamespace: number;
  /** Cloudflare account holding the media buckets; local development uses beta's. */
  mediaAccountId: string;
  /** Private bucket where uploads are processed and screened, and deleted after two hours. */
  mediaUploadsBucket: string;
  /** Private bucket that keeps uploads automod passed, and the files processing made from them. */
  privateMediaBucket: string;
  /** Public bucket of processed media, served from `mediaHost`. */
  mediaBucket: string;
  /** The host the public bucket is served from. */
  mediaHost: string;
  /**
   * The bucket every environment a reseed wipes shares: seed files' processed bytes by hash, which
   * a seed copies in rather than processing again. None in beta and production.
   */
  processedBucket?: string;
  /** Google Cloud project for uptime checks and the telemetry collector; previews use beta's. */
  googleCloudProject?: string;
  googleCloudRegion?: string;
}

export function parseEnvironment(value: string): Environment {
  if (value === "local" || value === "beta" || value === "production") {
    return { kind: value };
  }
  const preview = /^pr-([1-9]\d*)$/.exec(value);
  if (preview) return { kind: "preview", pr: PullRequestNumber.parse(Number(preview[1])) };
  const localStack = /^local-(.+)$/.exec(value);
  if (localStack?.[1])
    return { kind: "local", stack: { name: parseLocalStackName(localStack[1]) } };
  throw new Error(
    `invalid environment "${value}"; expected local, local-<stack>, beta, production, or pr-<number>`,
  );
}

/**
 * An environment's name: `local`, `local-<stack>` for a named local stack, `pr-<number>`, `beta`,
 * or `production`. Telemetry carries it as `deployment.environment.name`.
 */
export function environmentName(environment: Environment): string {
  if (environment.kind === "preview") return `pr-${PullRequestNumber.parse(environment.pr)}`;
  if (environment.kind === "local" && environment.stack?.name) {
    return `local-${environment.stack.name}`;
  }
  return environment.kind;
}

export function resolveEnvironment(environment: Environment): ResolvedEnvironment {
  const name = environmentName(environment);
  if (environment.kind === "local") {
    const stack = environment.stack ?? defaultLocalStack;
    const ports = localPorts(stack);
    const zone = localZone(stack);
    const worker = (app: string) => localWorkerName(stack, app);
    const database = localDatabaseName(stack);
    return {
      name,
      zone,
      host: zone,
      apiHost: `api.${zone}`,
      agentRunnerHost: `agent-runner.${zone}`,
      gameServerHost: `game-server.${zone}`,
      presetServerHost: `preset-server.${zone}`,
      assetEditorHost: `asset-editor.${zone}`,
      apiOrigin: `http://127.0.0.1:${ports.api}`,
      agentRunnerOrigin: `http://127.0.0.1:${ports.agentRunner}`,
      gameServerOrigin: `http://127.0.0.1:${ports.gameServer}`,
      presetServerOrigin: `http://127.0.0.1:${ports.presetServer}`,
      assetEditorOrigin: `http://127.0.0.1:${ports.assetEditor}`,
      agentRunnerWorker: worker("agent-runner"),
      gameServerWorker: worker("game-server"),
      presetServerWorker: worker("preset-server"),
      assetEditorWorker: worker("asset-editor"),
      apiWorker: worker("api"),
      webWorker: worker("web"),
      telemetryTailWorker: worker("telemetry-tail"),
      tilesWorker: worker("tiles"),
      tilesHost: `tiles.${zone}`,
      tilesOrigin: `https://tiles.${zone}`,
      otlpEndpoint: `http://127.0.0.1:${localServicePorts.collectorOtlp}`,
      gamesBucket: "pax-games",
      dbBranch: "local",
      databaseName: database,
      clickhouseDatabase: database,
      apiRateLimitNamespaceBase: 10_000,
      telemetryRateLimitNamespace: 1100,
      mediaAccountId: developmentMediaStorage.accountId,
      mediaUploadsBucket: developmentMediaStorage.mediaUploadsBucket,
      privateMediaBucket: developmentMediaStorage.privateMediaBucket,
      mediaBucket: developmentMediaStorage.mediaBucket,
      mediaHost: developmentMediaStorage.mediaHost,
      processedBucket: PROCESSED_BUCKET,
    };
  }

  if (environment.kind === "beta" || environment.kind === "production") {
    const base = cloudEnvironments[environment.kind];
    return {
      name,
      accountId: base.accountId,
      zoneId: base.zoneId,
      zone: base.zone,
      host: base.host,
      planetscaleOrganization: base.planetscaleOrganization,
      apiHost: `api.${base.zone}`,
      agentRunnerHost: `agent-runner.${base.zone}`,
      gameServerHost: `game-server.${base.zone}`,
      presetServerHost: `preset-server.${base.zone}`,
      assetEditorHost: `asset-editor.${base.zone}`,
      apiOrigin: `https://api.${base.zone}`,
      agentRunnerOrigin: `https://agent-runner.${base.zone}`,
      gameServerOrigin: `https://game-server.${base.zone}`,
      presetServerOrigin: `https://preset-server.${base.zone}`,
      assetEditorOrigin: `https://asset-editor.${base.zone}`,
      agentRunnerWorker: base.agentRunnerWorker,
      gameServerWorker: base.gameServerWorker,
      presetServerWorker: base.presetServerWorker,
      assetEditorWorker: base.assetEditorWorker,
      apiWorker: base.apiWorker,
      webWorker: base.webWorker,
      telemetryTailWorker: base.telemetryTailWorker,
      tilesWorker: base.tilesWorker,
      tilesHost: `tiles.${base.userContentZone}`,
      tilesOrigin: `https://tiles.${base.userContentZone}`,
      otlpEndpoint: telemetryCollectorOrigin(base),
      gamesBucket: base.gamesBucket,
      dbBranch: "main",
      databaseName: `pax-${environment.kind}`,
      clickhouseDatabase: "pax",
      apiRateLimitNamespaceBase: base.apiRateLimitNamespaceBase,
      telemetryRateLimitNamespace: base.telemetryRateLimitNamespace,
      mediaAccountId: base.accountId,
      mediaUploadsBucket: base.mediaUploadsBucket,
      privateMediaBucket: base.privateMediaBucket,
      mediaBucket: base.mediaBucket,
      mediaHost: base.userContentZone,
      googleCloudProject: base.googleCloudProject,
      googleCloudRegion: base.googleCloudRegion,
    };
  }

  const suffix = environmentName(environment);
  return {
    name,
    accountId: previewSubstrate.accountId,
    zoneId: previewSubstrate.zoneId,
    zone: previewSubstrate.zone,
    host: `${suffix}.${previewSubstrate.zone}`,
    planetscaleOrganization: previewSubstrate.planetscaleOrganization,
    // One label under the beta zone so the `*.pax-beta.co` DNS and Access
    // wildcards still cover it.
    apiHost: `api-${suffix}.${previewSubstrate.zone}`,
    agentRunnerHost: `agent-runner-${suffix}.${previewSubstrate.zone}`,
    gameServerHost: `game-server-${suffix}.${previewSubstrate.zone}`,
    presetServerHost: `preset-server-${suffix}.${previewSubstrate.zone}`,
    assetEditorHost: `asset-editor-${suffix}.${previewSubstrate.zone}`,
    apiOrigin: `https://api-${suffix}.${previewSubstrate.zone}`,
    agentRunnerOrigin: `https://agent-runner-${suffix}.${previewSubstrate.zone}`,
    gameServerOrigin: `https://game-server-${suffix}.${previewSubstrate.zone}`,
    presetServerOrigin: `https://preset-server-${suffix}.${previewSubstrate.zone}`,
    assetEditorOrigin: `https://asset-editor-${suffix}.${previewSubstrate.zone}`,
    agentRunnerWorker: `pax-agent-runner-${suffix}`,
    gameServerWorker: `pax-game-server-${suffix}`,
    presetServerWorker: `pax-preset-server-${suffix}`,
    assetEditorWorker: `pax-asset-editor-${suffix}`,
    apiWorker: `pax-api-${suffix}`,
    webWorker: `pax-web-${suffix}`,
    telemetryTailWorker: `pax-telemetry-tail-${suffix}`,
    tilesWorker: `pax-tiles-${suffix}`,
    // One label under the user-content zone, like the preview's other hosts under the beta zone.
    tilesHost: `tiles-${suffix}.${cloudEnvironments.beta.userContentZone}`,
    tilesOrigin: `https://tiles-${suffix}.${cloudEnvironments.beta.userContentZone}`,
    otlpEndpoint: previewSubstrate.otlpEndpoint,
    gamesBucket: `pax-games-${suffix}`,
    dbBranch: suffix,
    databaseName: previewSubstrate.databaseName,
    clickhouseDatabase: `pr_${environment.pr}`,
    apiRateLimitNamespaceBase: previewApiRateLimitNamespaceBase + environment.pr * 10,
    telemetryRateLimitNamespace: previewTelemetryRateLimitNamespaceBase + environment.pr,
    mediaAccountId: previewSubstrate.accountId,
    mediaUploadsBucket: previewSubstrate.mediaUploadsBucket,
    privateMediaBucket: previewSubstrate.privateMediaBucket,
    mediaBucket: previewSubstrate.mediaBucket,
    mediaHost: previewSubstrate.mediaHost,
    processedBucket: PROCESSED_BUCKET,
    googleCloudProject: previewSubstrate.googleCloudProject,
    googleCloudRegion: previewSubstrate.googleCloudRegion,
  };
}

const DeveloperHandle = z
  .string()
  .regex(
    /^[a-z0-9]([a-z0-9-]{0,18}[a-z0-9])?$/,
    "developer handle: 1-20 lowercase letters, digits, or hyphens",
  );

export function parseDeveloperHandle(value: string): string {
  return DeveloperHandle.parse(value);
}

/**
 * The prefix of every key an environment writes in the buckets it shares: none for production and
 * beta, `pr-{n}/` for a preview, and `local/{developer}/{machine}/{stack}/` for a laptop's stack, where
 * the default stack is `default`.
 */
export function blobNamespace(
  environment: Environment,
  local?: { developer: string; machine: string },
): string {
  if (environment.kind === "preview") return `${environmentName(environment)}/`;
  if (environment.kind !== "local") return "";
  if (!local) throw new Error("a local stack's storage needs its developer and machine");
  const stack = environment.stack?.name ?? "default";
  return `local/${parseDeveloperHandle(local.developer)}/${parseMachineId(local.machine)}/${stack}/`;
}

const MachineId = z.string().regex(/^[a-z0-9]{8}$/, "machine id: 8 lowercase letters or digits");

export function parseMachineId(value: string): string {
  return MachineId.parse(value);
}

/**
 * Where the tile server finds a published tile archive in the public bucket, with `{name}` for the
 * archive's hash in its URL: the server's `PMTILES_PATH`.
 */
export function basemapArchivePath(
  environment: Environment,
  local?: { developer: string; machine: string },
): string {
  return `${blobNamespace(environment, local)}{name}`;
}

/**
 * Tagged on every Stripe object an environment creates. Previews share one sandbox and local
 * stacks share another, so each acts only on objects carrying its own tag.
 */
export function stripeTarget(environment: Environment, developer?: string): string {
  if (environment.kind !== "local") return environmentName(environment);
  if (!developer) throw new Error("local Stripe objects need a developer handle");
  return `local-${localStackHandle(environment, developer)}`;
}

/** A developer's handle, followed by the stack's name for a named stack: `eli` or `eli-fix-ui`. */
function localStackHandle(environment: Environment & { kind: "local" }, developer: string): string {
  const handle = parseDeveloperHandle(developer);
  return environment.stack?.name ? `${handle}-${environment.stack.name}` : handle;
}

/**
 * Previews share one Stripe sandbox and developers share another, so each of their catalogs is
 * labelled with its target. Beta and production each have a Stripe sandbox to themselves.
 */
export function sharesStripeSandbox(environment: Environment): boolean {
  return environment.kind === "preview" || environment.kind === "local";
}

/** The lookup key of an offer's price: `pr-42.pack_small` in a shared sandbox, else `pack_small`. */
export function stripeLookupKey(environment: Environment, target: string, offerId: string): string {
  return sharesStripeSandbox(environment) ? `${target}.${offerId}` : offerId;
}

/** Identifies a target's Checkout Sessions and billing portal: `pax_store_` plus 8 letters. */
export function stripeIntegrationIdentifier(target: string): string {
  let hash = 2166136261;
  const source = `pax-store:${target}`;
  for (let index = 0; index < source.length; index++) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  let suffix = "";
  for (let index = 0; index < 8; index++) {
    hash = Math.imul(hash, 16777619) ^ (index + 1);
    suffix += "abcdefghijklmnopqrstuvwxyz"[(hash >>> 0) % 26];
  }
  return `pax_store_${suffix}`;
}

/** Whether a secret or restricted Stripe key is test-mode or live-mode, or undefined for any other key. */
export function stripeKeyMode(key: string): "test" | "live" | undefined {
  return /^[sr]k_(test|live)_/.exec(key)?.[1] as "test" | "live" | undefined;
}

/** Why an environment may not use a Stripe key, or undefined when it may. Only beta and production may use a live-mode key. */
export function stripeKeyProblem(key: string, environment: Environment): string | undefined {
  const name = environmentName(environment);
  const keyMode = stripeKeyMode(key);
  if (!keyMode) return `${name}'s Stripe key is not a secret or restricted key.`;
  const mayUseLiveKey = environment.kind === "beta" || environment.kind === "production";
  if (keyMode === "live" && !mayUseLiveKey) {
    return `${name} needs a test-mode Stripe key, but the key is live-mode.`;
  }
  return undefined;
}

/** The Stripe API version the API pins, which its webhook endpoints also render events in. */
export const stripeApiVersion = "2026-08-26.dahlia";

/** The events `POST /webhooks/stripe` acts on. Its endpoints subscribe to exactly these. */
export const stripeWebhookEvents = [
  "checkout.session.completed",
  "checkout.session.async_payment_succeeded",
  "checkout.session.async_payment_failed",
  "setup_intent.succeeded",
  "invoice.finalized",
  "invoice.paid",
  "invoice.payment_failed",
  "customer.subscription.created",
  "customer.subscription.updated",
  "customer.subscription.deleted",
  "charge.dispute.created",
  "charge.refunded",
] as const;

export const modalAppNames = ["media-processor"] as const;
export type ModalAppName = (typeof modalAppNames)[number];

export const modalWorkspaces = { beta: "pax-beta", production: "pax-production" } as const;

export interface ModalTarget {
  workspace: string;
  environment: "main" | "preview" | "dev";
  /** Appended to every app name so previews and local stacks never share an app. */
  appSuffix: string;
  /** Apps started by `modal serve` rather than deployed. */
  served: boolean;
}

export function modalTarget(environment: Environment, developer?: string): ModalTarget {
  if (environment.kind === "production") {
    return {
      workspace: modalWorkspaces.production,
      environment: "main",
      appSuffix: "",
      served: false,
    };
  }
  if (environment.kind === "beta") {
    return { workspace: modalWorkspaces.beta, environment: "main", appSuffix: "", served: false };
  }
  if (environment.kind === "preview") {
    return {
      workspace: modalWorkspaces.beta,
      environment: "preview",
      appSuffix: `-${environmentName(environment)}`,
      served: false,
    };
  }
  if (!developer) throw new Error("local Modal apps need a developer handle");
  return {
    workspace: modalWorkspaces.beta,
    environment: "dev",
    appSuffix: `-${localStackHandle(environment, developer)}`,
    served: true,
  };
}

export function modalAppName(target: ModalTarget, app: ModalAppName): string {
  return `${app}${target.appSuffix}`;
}

/** Each app serves one web endpoint whose label is its app name. */
export function modalEndpointOrigin(target: ModalTarget, app: ModalAppName): string {
  const environmentSuffix = target.environment === "main" ? "" : `-${target.environment}`;
  const label = `${modalAppName(target, app)}${target.served ? "-dev" : ""}`;
  return `https://${target.workspace}${environmentSuffix}--${label}.modal.run`;
}
